<i18n locale="en">
{
  "title": "Custom font upload agreement",
  "prompt": "I have read and agree to the custom font upload terms above.",
  "continue": "Agree and continue",
  "cancel": "Cancel",
  "errors": {
    "accept": "Unable to save acceptance",
    "missingAgreement": "Custom font agreement is unavailable"
  }
}
</i18n>

<template>
  <!--
    eslint-disable vue/no-v-html

    The agreement body is sourced from our internal legal collection
    so XSS risk is low.
  -->
  <OwnDialog :model-value="true" size="sm" @cancel="$emit('close')">
    <template #heading>
      <OwnDialogHeading :title="t('title')" @cancel="$emit('close')" />
    </template>

    <OwnDialogContent scroll>
      <div
        v-if="agreement"
        class="custom-font-agreement-modal__body"
        v-html="agreement.body"
      ></div>
      <OwnType
        v-else
        :text="t('errors.missingAgreement')"
        variant="paragraph-small"
        color="danger"
      />

      <div class="align-start margin-top-4 flex-row gap-2">
        <OwnCheckbox
          v-model="userHasAgreed"
          accessibility-id="custom-font-agreement"
          data-testid="custom-font-agreement-checkbox"
        />
        <label
          class="text-paragraph-small text-color-secondary cursor-pointer"
          for="custom-font-agreement"
          v-text="t('prompt')"
        ></label>
      </div>
    </OwnDialogContent>

    <OwnDialogFooter>
      <OwnButton
        :text="t('cancel')"
        variant="secondary"
        data-testid="custom-font-agreement-cancel-btn"
        @click="$emit('close')"
      />
      <OwnButton
        :disabled="!canSubmit"
        :processing="isProcessing"
        :text="t('continue')"
        variant="primary"
        data-testid="custom-font-agreement-submit-btn"
        @click="onSubmit"
      />
    </OwnDialogFooter>
  </OwnDialog>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { logError } from '@/logger'
import { OlympusClient } from '@/OlympusClient'
import { OwnButton } from '@/ui/OwnButton'
import { OwnCheckbox } from '@/ui/OwnCheckbox'
import {
  OwnDialog,
  OwnDialogContent,
  OwnDialogFooter,
  OwnDialogHeading,
} from '@/ui/OwnDialog'
import { OwnType } from '@/ui/OwnType'
import { useNotify } from '@/utils/useNotify'

type Agreement = Awaited<
  ReturnType<typeof OlympusClient.legal.v1.agreements.getAgreements>
>['results'][number]

const props = defineProps<{
  agreement: Agreement | null
  brandId: string
}>()

const emit = defineEmits<{ close: []; accepted: [] }>()

const { t } = useI18n()
const notify = useNotify()

const userHasAgreed = ref(false)
const isProcessing = ref(false)

const canSubmit = computed(
  () => Boolean(props.agreement) && userHasAgreed.value && !isProcessing.value
)

const onSubmit = async (): Promise<void> => {
  if (!props.agreement) {
    notify(t('errors.missingAgreement'), 'error')
    return
  }

  try {
    isProcessing.value = true
    await OlympusClient.legal.v1.acceptances.createAcceptance({
      body: {
        agreementId: props.agreement.id,
        subjectId: props.brandId,
        type: props.agreement.type,
      },
    })
    emit('accepted')
  } catch (error) {
    logError(error)
    notify(t('errors.accept'), 'error')
  } finally {
    isProcessing.value = false
  }
}
</script>

<style lang="scss" scoped>
.custom-font-agreement-modal__body {
  padding: 12px 0;
  color: var(--text-color-secondary);
}

.margin-top-4 {
  margin-top: 16px;
}
</style>
